import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { eventService } from '../../services/eventService';
import ImageUpload from '../common/ImageUpload';
import { societyService, extractSocietyResults } from '../../services/societyService';
import SocietySearchableSelect from '../common/SocietySearchableSelect';
import { getEventUrl } from '../../utils/urlHelpers';

const INITIAL_FORM_DATA = {
  name: '',
  venue: '',
  details: '',
  host: '',
  special_guest: '',
  contact_phone: '',
  category: 'community',
  society: '',
  is_public: false,
  start_date: '',
  start_time: '',
  end_date: '',
  end_time: '',
};

const formatTimeForInput = (value) => {
  if (!value) return '';
  const str = String(value);
  return str.length >= 5 ? str.slice(0, 5) : str;
};

const EventCreateForm = ({ initialEvent = null, isEditMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [userSociety, setUserSociety] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  // Load societies on component mount
  useEffect(() => {
    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);
        
        // Check if user is admin
        const userIsAdmin = user && (
          user.is_staff || 
          user.is_superuser || 
          ['admin', 'moderator'].includes(user.role)
        );
        setIsAdmin(userIsAdmin);
        
        if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const list = extractSocietyResults(response);
          setSocieties(list);
          const societyIdFromUrl = searchParams.get('society');
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = list.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({ ...prev, society: selected.id }));
            }
          } else if (list.length && !isEditMode) {
            setFormData((prev) => ({ ...prev, society: prev.society || list[0].id }));
          }
        } else if (user && user.society) {
          // Regular users can only use their own society
          setUserSociety(user.society);
          setSocieties([user.society]);
          setFormData(prev => ({
            ...prev,
            society: user.society.id
          }));
        } else {
          // User has no society, show error
          showError('You must be a member of a society to create events.');
          setSocieties([]);
        }
      } catch (error) {
        console.error('Error loading societies:', error);
        showError('Failed to load societies. Please try again.');
      } finally {
        setLoadingSocieties(false);
      }
    };

    if (user) {
      loadSocieties();
    }
  }, [showError, user, searchParams, isEditMode]);

  useEffect(() => {
    if (!initialEvent || !isEditMode) return;
    const societyId = initialEvent.society?.id ?? initialEvent.society;
    setFormData({
      name: initialEvent.name ?? '',
      venue: initialEvent.venue ?? '',
      details: initialEvent.details ?? '',
      host: initialEvent.host ?? '',
      special_guest: initialEvent.special_guest ?? '',
      contact_phone: initialEvent.contact_phone ?? '',
      category: initialEvent.category ?? 'community',
      society: societyId ?? '',
      is_public: !!initialEvent.is_public,
      start_date: initialEvent.start_date ?? '',
      start_time: formatTimeForInput(initialEvent.start_time),
      end_date: initialEvent.end_date ?? '',
      end_time: formatTimeForInput(initialEvent.end_time),
    });
  }, [initialEvent, isEditMode]);

  const getSocietySlugFromPath = () => {
    if (!location.pathname.includes('/societies/')) return null;
    const pathParts = location.pathname.split('/');
    const societySlugIndex = pathParts.indexOf('societies') + 1;
    return pathParts[societySlugIndex] || null;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.society) {
      showError('Please select a society for this event.');
      return;
    }

    setLoading(true);

    try {
      const eventData = {
        ...formData,
        society: formData.society,
        images: selectedImages,
      };
      const societySlug = getSocietySlugFromPath();

      if (isEditMode && initialEvent) {
        const eventKey = initialEvent.slug || initialEvent.id;
        const updated = await eventService.updateEvent(eventKey, eventData);
        showSuccess('Event updated successfully!');
        navigate(getEventUrl(updated || initialEvent, societySlug), { replace: true });
      } else {
        const created = await eventService.createEvent(eventData);
        showSuccess('Event created successfully! It will be reviewed by admin before being published.');
        const eventKey = created?.slug || created?.id;
        if (eventKey) {
          navigate(getEventUrl(created, societySlug), { replace: true });
        } else if (societySlug) {
          navigate(`/societies/${societySlug}/events`);
        } else {
          navigate('/events');
        }
      }
    } catch (error) {
      console.error('Error creating event:', error);
      const message =
        error?.message || 'Failed to create event. Please try again.';
      showError(typeof message === 'string' ? message : 'Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Must match backend Event.CATEGORY_CHOICES
  const categoryOptions = [
    { value: 'community', label: 'Community' },
    { value: 'islamic', label: 'Islamic Events' },
    { value: 'family', label: 'Family & Kids' },
    { value: 'sports', label: 'Sports' },
    { value: 'business', label: 'Business & Seminars' },
    { value: 'food', label: 'Food Festivals' },
    { value: 'concerts', label: 'Concerts' },
    { value: 'arts', label: 'Arts & Culture' },
    { value: 'tech', label: 'Tech & Startups' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditMode ? 'Edit Event' : 'Create New Event'}
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Society Selection - Only for admin users */}
          {isAdmin ? (
            <SocietySearchableSelect
              societies={societies}
              value={formData.society}
              onChange={handleChange}
              loading={loadingSocieties}
            />
          ) : (
            /* Show user's society for regular users */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Society
              </label>
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {userSociety ? `${userSociety.name} - ${userSociety.city}` : 'No society assigned'}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                You can only create events for your own society.
              </p>
            </div>
          )}

          {/* Event Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Event Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter event name"
            />
          </div>

          {/* Venue */}
          <div>
            <label htmlFor="venue" className="block text-sm font-medium text-gray-700 mb-2">
              Venue *
            </label>
            <input
              type="text"
              id="venue"
              name="venue"
              value={formData.venue}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter venue address"
            />
          </div>

          {/* Start date & time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                Start date
              </label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-2">
                Start time
              </label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* End date & time (optional) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                End date (optional)
              </label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mb-2">
                End time (optional)
              </label>
              <input
                type="time"
                id="end_time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Host */}
          <div>
            <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-2">
              Host/Organizer
            </label>
            <input
              type="text"
              id="host"
              name="host"
              value={formData.host}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter host or organizer name"
            />
          </div>

          {/* Special Guest */}
          <div>
            <label htmlFor="special_guest" className="block text-sm font-medium text-gray-700 mb-2">
              Special Guest
            </label>
            <input
              type="text"
              id="special_guest"
              name="special_guest"
              value={formData.special_guest}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter special guest name"
            />
          </div>

          {/* Contact Phone */}
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter contact phone number"
            />
          </div>

          {/* Details */}
          <div>
            <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
              Event Details
            </label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe the event details..."
            />
          </div>

          {/* Images */}
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={5}
            disabled={loading}
          />

          {/* Public Visibility */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_public"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
              Make this event public (visible to everyone)
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                const isSocietyContext = location.pathname.includes('/societies/');
                if (isSocietyContext) {
                  const pathParts = location.pathname.split('/');
                  const societySlugIndex = pathParts.indexOf('societies') + 1;
                  const societySlug = pathParts[societySlugIndex];
                  navigate(`/societies/${societySlug}/events`);
                } else {
                  navigate('/events');
                }
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Event' : 'Create Event')}
            </button>
          </div>
        </form>

        {/* Info Message */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Your event will be reviewed by an admin before being published and visible to the public.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCreateForm;
